import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { getFormattedErr } from 'src/helpers';
import apiClient from 'src/helpers/apiClient';
import { setSubmission } from '../slices/codingPractice';

export const fetchProblems = createAsyncThunk(
  'codingPractice/fetchProblems',
  async (
    {
      page,
      pageSize,
      difficultyLevel,
      tag,
      search,
    }: {
      page?: number;
      pageSize?: number;
      difficultyLevel?: number;
      tag?: string;
      search?: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await apiClient.getCodingPracticeApi()
      ).codingPracticeApiListProblems(
        { difficultyLevel, page, pageSize, search, tag },
        { signal },
      );
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchProblem = createAsyncThunk(
  'codingPractice/fetchProblem',
  async ({ slug }: { slug: string }, { rejectWithValue, signal }) => {
    try {
      const res = await (
        await apiClient.getCodingPracticeApi()
      ).codingPracticeApiGetProblem({ slug }, { signal });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchCategories = createAsyncThunk(
  'codingPractice/fetchCategories',
  async (_: void, { rejectWithValue, signal }) => {
    try {
      const res = await (
        await apiClient.getCodingPracticeApi()
      ).codingPracticeApiListCategories({ signal });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchCategory = createAsyncThunk(
  'codingPractice/fetchCategory',
  async ({ slug }: { slug: string }, { rejectWithValue, signal }) => {
    try {
      const res = await (
        await apiClient.getCodingPracticeApi()
      ).codingPracticeApiGetCategory({ slug }, { signal });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchProblemResponses = createAsyncThunk(
  'codingPractice/fetchProblemResponses',
  async ({ slug }: { slug: string }, { rejectWithValue, signal }) => {
    try {
      const res = await (
        await apiClient.getCodingPracticeApi()
      ).codingPracticeApiListProblemResponses({ slug }, { signal });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const evaluateCode = createAsyncThunk(
  'codingPractice/evaluateCode',
  async (
    {
      slug,
      languageId,
      sourceCode,
      mode,
    }: {
      slug: string;
      languageId: string;
      sourceCode: string;
      mode: 'run' | 'submit';
    },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getCodingPracticeApi();
      const body = { language_id: languageId, source_code: sourceCode };
      const res =
        mode === 'run'
          ? await api.codingPracticeApiRunCode({ slug, runSubmitBody: body })
          : await api.codingPracticeApiSubmitCode({
              slug,
              runSubmitBody: body,
            });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

const POLL_INTERVAL_MS = 1200;
const MAX_POLLS = 50;
const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// Polls the submission until the judge reaches a terminal state, pushing each
// snapshot into the slice so the console reflects Queued -> Running -> verdict.
export const pollSubmission = createAsyncThunk(
  'codingPractice/pollSubmission',
  async (
    { submissionId }: { submissionId: string },
    { dispatch, rejectWithValue, signal },
  ) => {
    try {
      const api = await apiClient.getCodingPracticeApi();
      for (let i = 0; i < MAX_POLLS; i += 1) {
        if (signal.aborted) return undefined;
        // Sequential by design: each poll must observe the latest judge state
        // before deciding whether to wait again.
        // eslint-disable-next-line no-await-in-loop
        const res = await api.codingPracticeApiGetSubmission(
          { submissionId },
          {
            signal,
          },
        );
        dispatch(setSubmission(res.data));
        if (res.data.status === 'Done' || res.data.status === 'Error') {
          return res.data;
        }
        // eslint-disable-next-line no-await-in-loop
        await delay(POLL_INTERVAL_MS);
      }
      return undefined;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);
